import apiService from './apiService';

export type DateRangeType = 'last_7_days' | 'last_30_days' | 'last_90_days' | 'last_12_months' | 'custom';

export interface DateRange {
  date_range: DateRangeType;
  startDate?: Date;
  endDate?: Date;
}

export interface AnalyticsOverviewData {
  total_recipients: number;
  unique_opens: number;
  total_opens: number;
  open_rate: number;
  opens_by_day: Array<{
    date: string;
    opens: number;
  }>;
  opens_by_segment: Array<{
    segment: string | null;
    opens: number;
    sends: number;
    open_rate: number;
    clicks: number;
    click_rate: number;
  }>;
  total_emails_sent?: number;
  average_open_rate?: number;
  total_scheduled_emails?: number;
  total_clicks?: number;
  total_unique_clicks?: number;
  average_click_rate?: number;
}

export interface EmailPerformanceData {
  email_id: number;
  email_name: string;
  subject: string;
  date_sent: string;
  total_recipients: number;
  unique_opens: number;
  open_rate: number;
  total_opens: number;
  average_time_to_open: number;
  segment_name: string | null;
  total_clicks: number;
  unique_clicks: number;
  unique_contacts: number;
  opens_by_day: Array<{
    date: string;
    opens: number;
  }>;
  sends_by_day: Array<{
    date: string;
    sends: number;
  }>;
  clicks_by_day: Array<{
    date: string;
    clicks: number;
  }>;
  // Group analytics fields (optional - only populated for group queries)
  client_ids?: number[];
  client_names?: string[];
}

export interface CustomReportData {
  report_data: any[];
  summary: any;
}

export interface Client {
  id: number;
  url: string;
  connect_name?: string;
  agency_name?: string;
}

export interface Email {
  id: number;
  name: string;
  subject: string | null;
  client_id: number;
  is_published?: boolean;
  scheduled_date?: string | null;
  segment_name?: string | null;
}

export interface AnalyticsOverviewParams {
  date_range: DateRangeType;
  client_id?: number;
  group_id?: number;
  start_date?: string;
  end_date?: string;
}

export interface EmailPerformanceParams {
  date_range: DateRangeType;
  client_id?: number;
  group_id?: number;
  start_date?: string;
  end_date?: string;
}

export interface EmailsParams {
  client_id: number;
  include_drafts: boolean;
}

export interface LandingPagesData {
  totalPageViews: number;
  totalUniqueUsers: number;
  averageEngagementTime: number;
  averageBounceRate: number;
  pages: Array<{
    pagePath: string;
    totalViews: number;
    uniqueUsers: number;
    newUsers: number;
    avgEngagementTime: number;
    bounceRate: number;
    engagedSessions: number;
    eventCount: number;
  }>;
  pageViewsByDay: Array<{
    date: string;
    pagePath: string;
    views: number;
  }>;
}

export interface LandingPagesParams {
  date_range: DateRangeType;
  client_id?: number;
  group_id?: number;
  start_date?: string;
  end_date?: string;
}

export async function fetchAnalyticsOverview(params: EmailPerformanceParams): Promise<AnalyticsOverviewData> {
  try {
    const response = await apiService.get<AnalyticsOverviewData>('/api/analytics/overview', {
      params: {
        date_range: params.date_range,
        client_id: params.client_id,
        start_date: params.start_date,
        end_date: params.end_date
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching analytics overview:', error);
    throw error;
  }
}

export async function fetchEmailPerformance(params: EmailPerformanceParams): Promise<EmailPerformanceData[]> {
  try {
    const response = await apiService.get<EmailPerformanceData[]>('/api/analytics/email-performance', { 
      params: {
        date_range: params.date_range,
        client_id: params.client_id,
        start_date: params.start_date,
        end_date: params.end_date
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching email performance:', error);
    throw error;
  }
}

export async function fetchClients(): Promise<Client[]> {
  try {
    const clientsResponse = await apiService.get<Client[]>('/api/clients');
    return clientsResponse.data;
  } catch (error) {
    console.error('Error fetching clients:', error);
    throw error;
  }
}

export async function fetchEmails(params: EmailsParams): Promise<Email[]> {
  try {
    const response = await apiService.get<Email[]>('/api/emails', {
      params: {
        ...params,
        include_drafts: false
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching emails:', error);
    throw error;
  }
}

export async function fetchEmailPerformanceData(params: EmailPerformanceParams): Promise<EmailPerformanceData[]> {
  try {
    const response = await apiService.get<EmailPerformanceData[]>('/api/analytics/email-performance', {
      params
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching email performance data:', error);
    throw error;
  }
}

export const fetchAllEmailPerformance = async (clientId: number, startDate?: string, endDate?: string, groupId?: number, includeSpam?: boolean): Promise<EmailPerformanceData[]> => {
  try {
    const params: any = {
      client_id: clientId,
      date_range: 'custom',
      start_date: startDate,
      end_date: endDate,
      include_spam: includeSpam
    };

    if (groupId) {
      params.group_id = groupId;
      delete params.client_id; // Remove client_id when using group_id
    }

    const response = await apiService.get<EmailPerformanceData[]>('/api/analytics/email-performance', {
      params,
      timeout: 30000 // Increase timeout to 30 seconds for analytics data
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching all email performance:', error);
    throw error;
  }
}; 

export async function fetchLandingPagesAnalytics(params: LandingPagesParams): Promise<LandingPagesData> {
  try {
    // Handle group analytics - use v2 endpoint if group_id is provided
    if (params.group_id && !params.client_id) {
      // Use v2 group landing pages endpoint
      // Convert ISO date strings to YYYY-MM-DD format for the backend
      const formatDate = (dateStr?: string) => {
        if (!dateStr) return undefined;
        // If it's already in YYYY-MM-DD format, return as is
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
        // Otherwise, extract date portion from ISO string
        return dateStr.split('T')[0];
      };
      
      const response = await apiService.get<LandingPagesData>('/api/v2/analytics/group-analytics/landing-pages', {
        params: {
          group_id: params.group_id.toString(),
          start_date: formatDate(params.start_date),
          end_date: formatDate(params.end_date)
        },
        timeout: 30000 // Increase timeout to 30 seconds for analytics data
      });
      return response.data;
    }
    
    // Regular endpoint requires client_id
    if (!params.client_id) {
      throw new Error('client_id is required for landing pages analytics');
    }
    
    const response = await apiService.get<LandingPagesData>('/api/analytics/landing-pages', {
      params: {
        date_range: params.date_range,
        client_id: params.client_id,
        start_date: params.start_date,
        end_date: params.end_date
      },
      timeout: 30000 // Increase timeout to 30 seconds for analytics data
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching landing pages analytics:', error);
    throw error;
  }
}

// New Email Analytics Interfaces
export interface EmailAnalyticsSummaryData {
  total_sends: number;
  total_opens: number;
  total_clicks: number;
  unique_opens?: number;
  unique_clicks?: number;
  clean_opens: number;
  clean_clicks: number;
  open_rate: number;
  click_rate: number;
  click_to_open_rate: number;
  unique_open_rate?: number;
  unique_click_rate?: number;
  unique_click_to_open_rate?: number;
  clean_open_rate: number;
  clean_click_rate: number;
  clean_click_to_open_rate: number;
  spam_open_count: number;
  spam_click_count: number;
  spam_open_rate: number;
  spam_click_rate: number;
}

export interface EmailCampaignPerformanceData {
  email_id: number;
  email_name: string;
  email_subject: string | null;
  sends: number;
  opens: number;
  clicks: number;
  clean_opens: number;
  clean_clicks: number;
  open_rate: number;
  click_rate: number;
  clean_open_rate: number;
  clean_click_rate: number;
  sent_date?: string;
  segment_name?: string;
}

export interface SpamAnalysisData {
  spam_reason: string;
  spam_count: number;
  spam_percentage: number;
  type: string; // 'opens' or 'clicks'
}

export interface EmailClickData {
  email_id: number;
  email_name: string;
  email_subject: string;
  email_segment: string | null;
  email_scheduled_date: string | null;
  emails_sent: number;
  total_hits: number;
  total_unique_hits: number;
  click_rate: number;
  unique_click_rate: number;
  click_details: Array<{
    id: number;
    redirect_id: number;
    url: string;
    hits: number;
    unique_hits: number;
    date_added: string | null;
    last_synced: string;
  }>;
}

export interface EngagementContact {
  contact_id: number;
  first_name: string | null;
  last_name: string | null;
  email_address: string | null;
  total_sent: number;
  total_opened: number;
  total_clicked: number;
  unique_opens: number;
  unique_clicks: number;
  last_activity: string | null;
  segment_name: string | null;
  latitude: number | null;
  longitude: number | null;
  city: string | null;
  state: string | null;
  country: string | null;
}

export interface EngagementResponse {
  contacts: EngagementContact[];
  total_count: number;
  page: number;
  limit: number;
  total_pages: number;
}

// V2 Engagement Service Function
export async function fetchEngagementDataV2(
  clientId: number,
  metric: 'opens' | 'clicks' = 'clicks',
  sortBy: 'top' | 'recent' = 'top',
  includeSpam: boolean = true,
  page: number = 1,
  limit: number = 25,
  startDate?: string,
  endDate?: string,
  emailAddress?: string,
  firstName?: string,
  lastName?: string,
  segmentName?: string,
  sortByField?: string,
  sortOrder: 'asc' | 'desc' = 'desc',
  includeGeographic: boolean = false
): Promise<EngagementResponse> {
  const params = new URLSearchParams({
    client_id: clientId.toString(),
    metric,
    sort_by: sortBy,
    include_spam: includeSpam.toString(),
    page: page.toString(),
    limit: limit.toString(),
    sort_order: sortOrder,
    include_geographic: includeGeographic.toString()
  });

  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (emailAddress) params.append('email_address', emailAddress);
  if (firstName) params.append('first_name', firstName);
  if (lastName) params.append('last_name', lastName);
  if (segmentName) params.append('segment_name', segmentName);
  if (sortByField) params.append('sort_by_field', sortByField);

  const response = await apiService.get<EngagementResponse>(`/api/v2/engagement/email-engagement-summary?${params.toString()}`);
  return response.data;
}

// New Email Analytics Service Functions
export async function fetchEmailAnalyticsSummary(
  clientId: number, 
  startDate?: string, 
  endDate?: string,
  includeSpam?: boolean
): Promise<EmailAnalyticsSummaryData> {
  try {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (includeSpam !== undefined) params.include_spam = includeSpam;

    const response = await apiService.get<EmailAnalyticsSummaryData>(
      `/api/email-analytics/summary/${clientId}`,
      { params, timeout: 30000 }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching email analytics summary:', error);
    throw error;
  }
}

export async function fetchEmailCampaignPerformance(
  clientId: number,
  startDate?: string,
  endDate?: string,
  limit: number = 50,
  includeSpam?: boolean
): Promise<EmailCampaignPerformanceData[]> {
  try {
    const params: any = { limit };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (includeSpam !== undefined) params.include_spam = includeSpam;

    const response = await apiService.get<EmailCampaignPerformanceData[]>(
      `/api/email-analytics/campaigns/${clientId}`,
      { params, timeout: 30000 }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching email campaign performance:', error);
    throw error;
  }
}

export async function fetchSpamAnalysis(
  clientId: number,
  startDate?: string,
  endDate?: string
): Promise<SpamAnalysisData[]> {
  try {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiService.get<SpamAnalysisData[]>(
      `/api/email-analytics/spam-analysis/${clientId}`,
      { params, timeout: 30000 }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching spam analysis:', error);
    throw error;
  }
}

export async function fetchEmailClickData(clientId: number, emailId: number, groupId?: number, includeSpam?: boolean): Promise<EmailClickData> {
  try {
    const params: any = {
      email_id: emailId
    };

    if (groupId) {
      params.group_id = groupId;
    } else {
      params.client_id = clientId;
    }

    if (includeSpam !== undefined) {
      params.include_spam = includeSpam;
    }

    const response = await apiService.get<EmailClickData>('/api/analytics/email-click-summary', {
      params,
      timeout: 30000
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching email click data:', error);
    throw error;
  }
}

// New Email Analytics Time-Series Functions
export async function fetchOpensByDay(
  clientId: number,
  startDate?: string,
  endDate?: string,
  includeSpam?: boolean,
  uniqueOnly?: boolean
): Promise<Array<{date: string, opens: number}>> {
  try {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (includeSpam !== undefined) params.include_spam = includeSpam;
    if (uniqueOnly !== undefined) params.unique_only = uniqueOnly;

    const response = await apiService.get<Array<{date: string, opens: number}>>(
      `/api/email-analytics/opens-by-day/${clientId}`,
      { params, timeout: 30000 }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching opens by day:', error);
    throw error;
  }
}

export async function fetchClicksByDay(
  clientId: number,
  startDate?: string,
  endDate?: string,
  includeSpam?: boolean,
  uniqueOnly?: boolean
): Promise<Array<{date: string, clicks: number}>> {
  try {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (includeSpam !== undefined) params.include_spam = includeSpam;
    if (uniqueOnly !== undefined) params.unique_only = uniqueOnly;

    const response = await apiService.get<Array<{date: string, clicks: number}>>(
      `/api/email-analytics/clicks-by-day/${clientId}`,
      { params, timeout: 30000 }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching clicks by day:', error);
    throw error;
  }
}

// Dual endpoints that return both total and unique data
export async function fetchOpensByDayDual(
  clientId: number,
  startDate?: string,
  endDate?: string,
  includeSpam?: boolean
): Promise<Array<{date: string, total_opens: number, unique_opens: number}>> {
  try {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (includeSpam !== undefined) params.include_spam = includeSpam;

    const response = await apiService.get<Array<{date: string, total_opens: number, unique_opens: number}>>(
      `/api/email-analytics/opens-by-day-dual/${clientId}`,
      { params, timeout: 30000 }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching opens by day dual:', error);
    throw error;
  }
}

export async function fetchClicksByDayDual(
  clientId: number,
  startDate?: string,
  endDate?: string,
  includeSpam?: boolean
): Promise<Array<{date: string, total_clicks: number, unique_clicks: number}>> {
  try {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (includeSpam !== undefined) params.include_spam = includeSpam;

    const response = await apiService.get<Array<{date: string, total_clicks: number, unique_clicks: number}>>(
      `/api/email-analytics/clicks-by-day-dual/${clientId}`,
      { params, timeout: 30000 }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching clicks by day dual:', error);
    throw error;
  }
}

export async function fetchComprehensiveAnalytics(
  clientId: number,
  startDate?: string,
  endDate?: string,
  includeSpam?: boolean,
  uniqueOnly?: boolean
): Promise<{
  summary: {
    total_sends: number;
    total_opens: number;
    total_clicks: number;
    unique_opens: number;
    unique_clicks: number;
    open_rate: number;
    click_rate: number;
    click_to_open_rate: number;
    unique_open_rate: number;
    unique_click_rate: number;
    unique_click_to_open_rate: number;
  };
  time_series: Array<{
    date: string;
    total_opens: number;
    unique_opens: number;
    total_clicks: number;
    unique_clicks: number;
    total_sends: number;
  }>;
  segments: {
    opens: Array<{segment: string, opens: number}>;
    clicks: Array<{segment: string, clicks: number}>;
    sends: Array<{segment: string, sends: number}>;
    open_rates: Array<{segment: string, rate: number}>;
    click_rates: Array<{segment: string, click_rate: number, click_to_open_rate: number}>;
  };
}> {
  try {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (includeSpam !== undefined) params.include_spam = includeSpam;
    if (uniqueOnly !== undefined) params.unique_only = uniqueOnly;

    const response = await apiService.get(
      `/api/email-analytics/comprehensive/${clientId}`,
      { params, timeout: 30000 }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching comprehensive analytics:', error);
    throw error;
  }
}

export async function fetchSendsByDay(
  clientId: number,
  startDate?: string,
  endDate?: string,
  includeSpam?: boolean
): Promise<Array<{date: string, sends: number}>> {
  try {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (includeSpam !== undefined) params.include_spam = includeSpam;

    const response = await apiService.get<Array<{date: string, sends: number}>>(
      `/api/email-analytics/sends-by-day/${clientId}`,
      { params, timeout: 30000 }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching sends by day:', error);
    throw error;
  }
}

export async function fetchOpensBySegment(
  clientId: number,
  startDate?: string,
  endDate?: string,
  includeSpam?: boolean,
  uniqueOnly?: boolean
): Promise<Array<{
  segment: string;
  opens: number;
  sends: number;
  open_rate: number;
  clicks: number;
  click_rate: number;
}>> {
  try {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (includeSpam !== undefined) params.include_spam = includeSpam;
    if (uniqueOnly !== undefined) params.unique_only = uniqueOnly;

    const response = await apiService.get<Array<{
      segment: string;
      opens: number;
      sends: number;
      open_rate: number;
      clicks: number;
      click_rate: number;
    }>>(
      `/api/email-analytics/opens-by-segment/${clientId}`,
      { params, timeout: 30000 }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching opens by segment:', error);
    throw error;
  }
}

/**
 * Fetch segment summary data from V2 endpoint (uses daily_segment_summary table)
 * Returns engagement metrics aggregated by segment_name
 */
export async function fetchSegmentSummaryV2(
  clientId: number,
  startDate?: string,
  endDate?: string,
  includeSpam?: boolean
): Promise<{
  segments: Array<{
    segment_name: string;
    total_sent: number;
    total_opened: number;
    total_clicks_unfiltered: number;
    total_clicks_ip_filtered: number;
    unique_opens: number;
    unique_clicks_unfiltered: number;
    unique_clicks_ip_filtered: number;
    open_rate: number;
    click_rate_unfiltered: number;
    click_rate_ip_filtered: number;
    click_to_open_rate_unfiltered: number;
    click_to_open_rate_ip_filtered: number;
  }>;
  total_count: number;
}> {
  try {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (includeSpam !== undefined) params.include_spam = includeSpam;

    const response = await apiService.get(
      `/api/v2/analytics/segment-summary/${clientId}`,
      { params, timeout: 30000 }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching segment summary V2:', error);
    throw error;
  }
}

/**
 * Fetch email performance summary from V2 endpoint (uses email_performance_summary table)
 * Returns performance metrics for individual emails
 */
export async function fetchEmailPerformanceSummaryV2(
  clientId: number,
  startDate?: string,
  endDate?: string,
  includeSpam?: boolean
): Promise<{
  emails: Array<{
    id: number;
    client_id: number;
    email_id: number;
    email_name: string;
    subject: string | null;
    segment_name: string | null;
    scheduled_date: string | null;
    total_sent: number;
    total_opened: number;
    click_count_unfiltered: number;
    click_count_ip_filtered: number;
    unique_opens: number;
    unique_click_count_unfiltered: number;
    unique_click_count_ip_filtered: number;
    open_rate: number;
    click_rate_unfiltered: number;
    click_rate_ip_filtered: number;
    click_to_open_rate_unfiltered: number;
    click_to_open_rate_ip_filtered: number;
    first_sent: string | null;
    last_sent: string | null;
    opens_by_day: Array<{date: string; opens: number}>;
    sends_by_day: Array<{date: string; sends: number}>;
    clicks_by_day: Array<{date: string; clicks: number}>;
  }>;
  total_count: number;
}> {
  try {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (includeSpam !== undefined) params.include_spam = includeSpam;

    const response = await apiService.get(
      `/api/v2/analytics/email-performance-summary/${clientId}`,
      { params, timeout: 30000 }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching email performance summary V2:', error);
    throw error;
  }
}

/**
 * Fetch daily email summary from V2 endpoint (uses daily_analytics_summary table)
 * Returns daily email engagement metrics (sends, opens, clicks) aggregated by day
 */
export async function fetchDailyEmailSummaryV2(
  clientId: number,
  startDate?: string,
  endDate?: string,
  includeSpam?: boolean
): Promise<{
  summary: {
    total_sends: number;
    total_opens: number;
    total_clicks: number;
    unique_opens: number;
    unique_clicks: number;
    open_rate: number;
    click_rate: number;
    click_to_open_rate: number;
  };
  time_series: Array<{
    date: string;
    total_opens: number;
    unique_opens: number;
    total_clicks: number;
    unique_clicks: number;
    total_sends: number;
  }>;
}> {
  try {
    const params: any = {
      client_id: clientId,
      limit: 5000  // Get all days in range
    };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (includeSpam !== undefined) params.include_spam = includeSpam;

    const response = await apiService.get(
      `/api/v2/analytics/daily-email-summary`,
      { params, timeout: 30000 }
    );
    
    console.log('fetchDailyEmailSummaryV2 API response:', {
      params,
      response_data: response.data,
      records_count: response.data.records?.length,
      sample_record: response.data.records?.[0]
    });
    
    const records = response.data.records || [];
    
    // Calculate summary totals
    const summary = {
      total_sends: records.reduce((sum: number, r: any) => sum + r.total_sent, 0),
      total_opens: records.reduce((sum: number, r: any) => sum + r.total_opened, 0),
      total_clicks: records.reduce((sum: number, r: any) => sum + (includeSpam ? r.total_clicks_unfiltered : r.total_clicks_ip_filtered), 0),
      unique_opens: records.reduce((sum: number, r: any) => sum + r.unique_opens, 0),
      unique_clicks: records.reduce((sum: number, r: any) => sum + (includeSpam ? r.unique_clicks_unfiltered : r.unique_clicks_ip_filtered), 0),
      open_rate: 0,
      click_rate: 0,
      click_to_open_rate: 0
    };
    
    // Calculate rates
    if (summary.total_sends > 0) {
      summary.open_rate = (summary.total_opens / summary.total_sends) * 100;
      summary.click_rate = (summary.total_clicks / summary.total_sends) * 100;
    }
    if (summary.total_opens > 0) {
      summary.click_to_open_rate = (summary.total_clicks / summary.total_opens) * 100;
    }
    
    // Transform to time_series format
    const time_series = records.map((r: any) => ({
      date: r.date,
      total_opens: r.total_opened,
      unique_opens: r.unique_opens,
      total_clicks: includeSpam ? r.total_clicks_unfiltered : r.total_clicks_ip_filtered,
      unique_clicks: includeSpam ? r.unique_clicks_unfiltered : r.unique_clicks_ip_filtered,
      total_sends: r.total_sent
    }));
    
    return {
      summary,
      time_series
    };
  } catch (error) {
    console.error('Error fetching daily email summary V2:', error);
    throw error;
  }
}

// V2 Geographic Analytics Interfaces
export interface GeographicLocation {
  id: number;
  client_id: number;
  email_id: number | null;
  latitude: number;
  longitude: number;
  city: string;
  state: string;
  country: string;
  open_count: number;
  click_count: number;
  unique_opens: number;
  unique_clicks: number;
  date: string;
}

export interface GeographicAnalyticsResponse {
  locations: GeographicLocation[];
  total_count: number;
  page: number;
  limit: number;
  total_pages: number;
}

// V2 Geographic Analytics Service Function
export async function fetchGeographicAnalyticsV2(
  clientId: number,
  startDate?: string,
  endDate?: string,
  includeSpam: boolean = false,
  page: number = 1,
  limit: number = 1000
): Promise<GeographicAnalyticsResponse> {
  const params = new URLSearchParams({
    client_id: clientId.toString(),
    include_spam: includeSpam.toString(),
    page: page.toString(),
    limit: limit.toString()
  });

  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  const response = await apiService.get<GeographicAnalyticsResponse>(`/api/v2/analytics/geographic?${params.toString()}`);
  return response.data;
}

// Group Analytics Service Functions
export interface GroupMetricsRecord {
  date: string;
  total_clients: number;
  total_sent: number;
  total_opened: number;
  total_clicks: number;
  unique_opens: number;
  unique_clicks: number;
  bounces: number;
  complaints: number;
  open_rate: number;
  click_rate: number;
  click_to_open_rate: number;
}

export interface GroupPerformanceData {
  group_id: number;
  group_name: string;
  date_range: {
    start: string;
    end: string;
  };
  metrics: GroupMetricsRecord[];
}

export interface GroupSummaryTotals {
  total_sent: number;
  total_opened: number;
  total_clicks: number;
  unique_opens: number;
  unique_clicks: number;
}

export interface GroupSummaryAverages {
  avg_open_rate: number;
  avg_click_rate: number;
  avg_click_to_open_rate: number;
}

export interface GroupSummaryClients {
  min_contributing: number;
  max_contributing: number;
  avg_per_day: number;
  client_names: string[];
}

export interface GroupSummaryData {
  group_id: number;
  group_name: string;
  date_range: {
    start: string;
    end: string;
  };
  totals: GroupSummaryTotals;
  averages: GroupSummaryAverages;
  clients: GroupSummaryClients;
}

export async function fetchGroupPerformance(
  groupId: number,
  startDate?: string,
  endDate?: string,
  includeSpam: boolean = false
): Promise<GroupPerformanceData> {
  try {
    const params: any = {
      group_id: groupId.toString(),
      include_spam: includeSpam.toString()
    };
    
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiService.get<GroupPerformanceData>(
      '/api/v2/analytics/group-analytics/performance',
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching group performance:', error);
    throw error;
  }
}

export async function fetchGroupSummary(
  groupId: number,
  startDate?: string,
  endDate?: string,
  includeSpam: boolean = false
): Promise<GroupSummaryData> {
  try {
    const params: any = {
      group_id: groupId.toString(),
      include_spam: includeSpam.toString()
    };
    
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiService.get<GroupSummaryData>(
      '/api/v2/analytics/group-analytics/summary',
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching group summary:', error);
    throw error;
  }
}

export async function fetchGroupEmailPerformance(
  groupId: number,
  startDate?: string,
  endDate?: string,
  includeSpam: boolean = false
): Promise<EmailPerformanceData[]> {
  try {
    const params: any = {
      group_id: groupId.toString(),
      include_spam: includeSpam.toString()
    };
    
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiService.get<EmailPerformanceSummaryData>(
      '/api/v2/analytics/group-analytics/email-performance',
      { params }
    );
    
    // Transform to match EmailPerformanceData interface
    return response.data.emails.map((email: any) => {
      // Handle date conversion - scheduled_date and first_sent can be date objects or strings
      let dateSent = '';
      if (email.scheduled_date) {
        dateSent = typeof email.scheduled_date === 'string' ? email.scheduled_date : email.scheduled_date.toString();
      } else if (email.first_sent) {
        dateSent = typeof email.first_sent === 'string' ? email.first_sent : email.first_sent.toString();
      }
      
      return {
        email_id: email.email_id,
        email_name: email.email_name,
        subject: email.subject || '',
        date_sent: dateSent,
        total_recipients: email.total_sent,
        unique_opens: email.unique_opens,
        open_rate: email.open_rate,
        total_opens: email.total_opened,
        average_time_to_open: 0,
        segment_name: email.segment_name,
        total_clicks: includeSpam ? email.click_count_unfiltered : email.click_count_ip_filtered,
        unique_clicks: includeSpam ? email.unique_click_count_unfiltered : email.unique_click_count_ip_filtered,
        unique_contacts: email.total_sent, // Approximation
        opens_by_day: email.opens_by_day || [],
        sends_by_day: email.sends_by_day || [],
        clicks_by_day: email.clicks_by_day || [],
        client_ids: email.client_ids,
        client_names: email.client_names
      };
    });
  } catch (error) {
    console.error('Error fetching group email performance:', error);
    throw error;
  }
}

// Add this interface if it doesn't exist
export interface EmailPerformanceSummaryData {
  emails: EmailPerformanceData[];
  total_count: number;
}

export interface GroupSegmentSummaryRecord {
  segment_name: string;
  date: string;
  total_sent: number;
  total_opened: number;
  total_clicks_unfiltered: number;
  total_clicks_ip_filtered: number;
  unique_opens: number;
  unique_clicks_unfiltered: number;
  unique_clicks_ip_filtered: number;
  open_rate: number;
  click_rate_unfiltered: number;
  click_rate_ip_filtered: number;
  click_to_open_rate_unfiltered: number;
  click_to_open_rate_ip_filtered: number;
}

export interface GroupSegmentSummaryData {
  segments: GroupSegmentSummaryRecord[];
  total_count: number;
}

export async function fetchGroupSegmentSummary(
  groupId: number,
  startDate?: string,
  endDate?: string,
  includeSpam: boolean = false
): Promise<GroupSegmentSummaryData> {
  try {
    const params: any = {
      group_id: groupId.toString(),
      include_spam: includeSpam.toString()
    };
    
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiService.get<GroupSegmentSummaryData>(
      '/api/v2/analytics/group-analytics/segment-summary',
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching group segment summary:', error);
    throw error;
  }
}
